import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Layout, Code, Copy, Trash2, Plus, X, Eye, Settings, Palette,
  MessageCircle, Users, Building2, Ticket, Monitor, Smartphone,
  Check, RefreshCw, ChevronDown, ChevronRight, Globe, Layers,
  ToggleLeft, ToggleRight, ExternalLink, Zap, Info, ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// ─── OkBot Logo Icon (used in place of generic bot icon) ─────────────────────
const OkBotLogoIcon = ({ size = 22 }) => (
  <img src="/okbot_logo_2026.png" alt="OkBot" style={{ width: size, height: size, objectFit: 'contain', borderRadius: 4 }} />
);

// ─── Widget Type Definitions ─────────────────────────────────────────────────
const WIDGET_TYPES = [
  {
    id: 'bot_button',
    label: 'Botón del Bot',
    icon: OkBotLogoIcon,
    color: 'red',
    bg: 'bg-red-500/20',
    border: 'border-red-500/40',
    iconColor: 'text-red-400',
    description: 'Botón flotante que al hacer clic abre el chat principal de OkBot. Se puede colocar en cualquiera de las 4 esquinas de la pantalla.',
    badge: 'Chat IA',
    configFields: ['position', 'primaryColor', 'buttonText', 'welcomeMessage', 'theme', 'allowedDomains']
  },
  {
    id: 'okuser',
    label: 'Ventana OkUser',
    icon: Users,
    color: 'blue',
    bg: 'bg-blue-500/20',
    border: 'border-blue-500/40',
    iconColor: 'text-blue-400',
    description: 'Widget completo de acceso para usuarios finales. Permite login, registro y acceso a todas las funciones de OkBot para usuarios.',
    badge: 'Portal Usuario',
    configFields: ['theme', 'primaryColor', 'width', 'height', 'language', 'welcomeMessage', 'allowedDomains']
  },
  {
    id: 'oknegocios',
    label: 'Ventana OkNegocios',
    icon: Building2,
    color: 'purple',
    bg: 'bg-purple-500/20',
    border: 'border-purple-500/40',
    iconColor: 'text-purple-400',
    description: 'Widget completo de acceso para negocios. Permite login empresarial y acceso a todas las funciones de OkBot para negocios.',
    badge: 'Portal Negocio',
    configFields: ['theme', 'primaryColor', 'width', 'height', 'language', 'welcomeMessage', 'allowedDomains']
  },
  {
    id: 'support_user',
    label: 'Soporte para Usuarios',
    icon: Ticket,
    color: 'green',
    bg: 'bg-green-500/20',
    border: 'border-green-500/40',
    iconColor: 'text-green-400',
    description: 'Widget exclusivo de soporte para usuarios finales. Si no está logueado muestra primero el login; al ingresar accede directamente a la sección de tickets de soporte.',
    badge: 'Soporte Usuario',
    configFields: ['theme', 'primaryColor', 'width', 'height', 'welcomeMessage', 'allowedDomains']
  },
  {
    id: 'support_business',
    label: 'Soporte para Negocios',
    icon: Ticket,
    color: 'orange',
    bg: 'bg-orange-500/20',
    border: 'border-orange-500/40',
    iconColor: 'text-orange-400',
    description: 'Widget exclusivo de soporte para negocios. Si no está logueado muestra primero el login empresarial; al ingresar accede directamente a la gestión de tickets.',
    badge: 'Soporte Negocio',
    configFields: ['theme', 'primaryColor', 'width', 'height', 'welcomeMessage', 'allowedDomains']
  }
];

const POSITIONS = [
  { id: 'bottom-right', label: 'Abajo Derecha', icon: '↘' },
  { id: 'bottom-left', label: 'Abajo Izquierda', icon: '↙' },
  { id: 'top-right', label: 'Arriba Derecha', icon: '↗' },
  { id: 'top-left', label: 'Arriba Izquierda', icon: '↖' }
];

const DEFAULT_WIDGET = {
  name: '',
  widget_type: 'bot_button',
  theme: 'dark',
  primary_color: '#ef4444',
  position: 'bottom-right',
  welcome_message: '¡Hola! ¿En qué puedo ayudarte?',
  button_text: 'Chatea con OkBot',
  width: '400',
  height: '600',
  language: 'es',
  allowed_domains: '',
  is_active: true
};

// ─── Widget Preview ───────────────────────────────────────────────────────────
const WidgetPreview = ({ type, config }) => {
  const typeDef = WIDGET_TYPES.find(t => t.id === type);
  if (!type || !typeDef) return null;

  const color = config.primary_color || '#ef4444';
  const isDark = config.theme === 'dark';
  const bg = isDark ? '#0f1117' : '#f8f9fa';
  const cardBg = isDark ? '#1a1d2e' : '#ffffff';
  const textPrimary = isDark ? '#ffffff' : '#111827';
  const textSecondary = isDark ? '#9ca3af' : '#6b7280';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';

  const mockWidth = type === 'bot_button' ? 320 : Math.min(parseInt(config.width) || 400, 360);
  const mockHeight = type === 'bot_button' ? 480 : Math.min(parseInt(config.height) || 600, 400);

  return (
    <div className="relative" style={{ width: mockWidth, maxWidth: '100%' }}>
      {/* Browser chrome */}
      <div className="rounded-xl overflow-hidden shadow-2xl border" style={{ borderColor }}>
        {/* Title bar */}
        <div className="flex items-center gap-2 px-3 py-2" style={{ backgroundColor: isDark ? '#1e2030' : '#e5e7eb' }}>
          <div className="flex gap-1.5">
            {['#ff5f57', '#febc2e', '#28c840'].map((c, i) => (
              <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex-1 mx-2">
            <div className="h-5 rounded text-xs flex items-center px-2" style={{ backgroundColor: isDark ? '#0f1117' : '#f3f4f6', color: textSecondary }}>
              okbot.mx
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ backgroundColor: bg, height: mockHeight, position: 'relative', overflow: 'hidden' }}>
          {type === 'bot_button' && (
            <>
              {/* Chat window */}
              <div style={{
                position: 'absolute',
                bottom: 80, right: 16,
                width: 260, height: 320,
                backgroundColor: cardBg,
                borderRadius: 16,
                border: `1px solid ${borderColor}`,
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
              }}>
                {/* Chat header */}
                <div style={{ backgroundColor: color, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src="/okbot_logo_2026.png" alt="OkBot" style={{ width: 22, height: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>OkBot</div>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>En línea</div>
                  </div>
                  <div style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.7)', fontSize: 18, cursor: 'pointer' }}>×</div>
                </div>
                {/* Messages */}
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', borderRadius: 12, padding: '8px 12px', maxWidth: '80%' }}>
                    <p style={{ color: textPrimary, fontSize: 12, margin: 0, lineHeight: 1.4 }}>{config.welcome_message || '¡Hola! ¿En qué puedo ayudarte?'}</p>
                  </div>
                  <div style={{ backgroundColor: color, borderRadius: 12, padding: '8px 12px', maxWidth: '75%', alignSelf: 'flex-end' }}>
                    <p style={{ color: '#fff', fontSize: 12, margin: 0 }}>Hola, necesito ayuda</p>
                  </div>
                  <div style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', borderRadius: 12, padding: '8px 12px', maxWidth: '80%' }}>
                    <p style={{ color: textPrimary, fontSize: 12, margin: 0 }}>¡Por supuesto! ¿En qué te ayudo?</p>
                  </div>
                </div>
                {/* Input */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 10, borderTop: `1px solid ${borderColor}`, display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1, height: 32, borderRadius: 16, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', border: `1px solid ${borderColor}` }} />
                  <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 14 }}>→</span>
                  </div>
                </div>
              </div>
              {/* Button */}
              <div style={{
                position: 'absolute',
                [config.position?.includes('bottom') ? 'bottom' : 'top']: 16,
                [config.position?.includes('right') ? 'right' : 'left']: 16,
                backgroundColor: color,
                borderRadius: 28,
                padding: '10px 16px',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: `0 4px 20px ${color}60`,
                cursor: 'pointer'
              }}>
                <img src="/okbot_logo_2026.png" alt="OkBot" style={{ width: 22, height: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {config.button_text || 'Chatea con OkBot'}
                </span>
              </div>
            </>
          )}

          {(type === 'okuser' || type === 'oknegocios') && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', borderRadius: 16 }}>
              <iframe
                src={type === 'okuser'
                  ? `${window.location.origin}/?widget=okuser&wid=preview&color=${(color || '#ef4444').replace('#', '')}`
                  : `${window.location.origin}/oknegocios?widget=oknegocios&wid=preview&color=${(color || '#ef4444').replace('#', '')}`
                }
                title={type === 'okuser' ? 'Vista previa — OkUser Login' : 'Vista previa — OkNegocios Login'}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="microphone; camera"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              />
            </div>
          )}

          {(type === 'support_user' || type === 'support_business') && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: cardBg }}>
              {/* Header */}
              <div style={{ backgroundColor: color, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src="/okbot_logo_2026.png" alt="OkBot" style={{ width: 32, height: 32, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                <div>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Centro de Soporte</div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>
                    {type === 'support_user' ? 'Soporte para usuarios' : 'Gestión de tickets de negocios'}
                  </div>
                </div>
              </div>
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {type === 'support_user' ? (
                  <>
                    {/* New ticket button */}
                    <div style={{ height: 36, borderRadius: 8, backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>+ Crear Nuevo Ticket</span>
                    </div>
                    {/* Ticket mock list */}
                    {[
                      { id: '#001', subject: 'Problema con mi cuenta', status: 'En Progreso', color: '#3b82f6' },
                      { id: '#002', subject: 'Consulta sobre pagos', status: 'Abierto', color: '#eab308' }
                    ].map(t => (
                      <div key={t.id} style={{ padding: '10px 12px', borderRadius: 10, border: `1px solid ${borderColor}`, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: textPrimary, fontSize: 12, fontWeight: 500 }}>{t.subject}</span>
                          <span style={{ fontSize: 10, color: '#fff', backgroundColor: t.color, borderRadius: 4, padding: '2px 6px' }}>{t.status}</span>
                        </div>
                        <span style={{ color: textSecondary, fontSize: 10, marginTop: 4, display: 'block' }}>{t.id}</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {/* Business ticket management */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['Abiertos (3)', 'En Progreso (1)', 'Resueltos (8)'].map((label, i) => (
                        <div key={i} style={{ flex: 1, padding: '6px 4px', borderRadius: 8, backgroundColor: i === 0 ? color : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'), textAlign: 'center' }}>
                          <span style={{ fontSize: 9, color: i === 0 ? '#fff' : textSecondary, fontWeight: 600 }}>{label}</span>
                        </div>
                      ))}
                    </div>
                    {[
                      { id: '#T-001', subject: 'Error en integración API', user: 'Carlos M.', status: 'Abierto' },
                      { id: '#T-002', subject: 'Acceso al panel de analytics', user: 'María P.', status: 'En Progreso' }
                    ].map(t => (
                      <div key={t.id} style={{ padding: '10px 12px', borderRadius: 10, border: `1px solid ${borderColor}`, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: textPrimary, fontSize: 11, fontWeight: 500 }}>{t.subject}</span>
                          <span style={{ fontSize: 9, color: '#fff', backgroundColor: color, borderRadius: 4, padding: '2px 5px' }}>{t.status}</span>
                        </div>
                        <span style={{ color: textSecondary, fontSize: 10 }}>{t.id} • {t.user}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="text-center text-gray-600 text-xs mt-2">Vista previa del widget</p>
    </div>
  );
};

// ─── Widget Creator ───────────────────────────────────────────────────────────
const WidgetCreator = ({ onCreated, onCancel }) => {
  const [step, setStep] = useState(1); // 1=choose type, 2=configure
  const [form, setForm] = useState({ ...DEFAULT_WIDGET });
  const [creating, setCreating] = useState(false);

  const selectedType = WIDGET_TYPES.find(t => t.id === form.widget_type);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Escribe un nombre para el widget'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/widgets`, {
        name: form.name,
        widget_type: form.widget_type,
        theme: form.theme,
        primary_color: form.primary_color,
        position: form.position,
        welcome_message: form.welcome_message,
        button_text: form.button_text,
        width: parseInt(form.width) || 400,
        height: parseInt(form.height) || 600,
        language: form.language,
        allowed_domains: form.allowed_domains ? form.allowed_domains.split(',').map(d => d.trim()).filter(Boolean) : [],
        is_active: true
      });
      toast.success('Widget creado exitosamente');
      onCreated(res.data);
    } catch (e) {
      toast.error('Error al crear widget');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={onCancel} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
          <ArrowLeft size={18} className="text-gray-400" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-white">Crear Nuevo Widget</h2>
          <p className="text-gray-500 text-sm">
            {step === 1 ? 'Selecciona el tipo de widget' : `Configurando: ${selectedType?.label}`}
          </p>
        </div>
        {/* Step indicator */}
        <div className="ml-auto flex items-center gap-2">
          {[1, 2].map(s => (
            <div key={s} className={`flex items-center gap-2 ${s < step ? 'cursor-pointer' : ''}`} onClick={() => s < step && setStep(s)}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                s === step ? 'bg-red-500 text-white' : s < step ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-500'
              }`}>{s < step ? <Check size={12} /> : s}</div>
              {s < 2 && <div className={`w-8 h-0.5 ${step > s ? 'bg-green-500' : 'bg-white/10'}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* STEP 1: Choose Type */}
      {step === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {WIDGET_TYPES.map(type => {
            const Icon = type.icon;
            const isSelected = form.widget_type === type.id;
            return (
              <button
                key={type.id}
                onClick={() => { setForm({ ...form, widget_type: type.id }); }}
                className={`glass-panel p-5 text-left transition-all hover:scale-[1.02] ${
                  isSelected ? `border-2 border-${type.color}-500 bg-${type.color}-500/5` : 'border border-white/10 hover:border-white/20'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl ${type.bg} flex items-center justify-center mb-3`}>
                  <Icon size={22} className={type.iconColor} />
                </div>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-white font-semibold text-sm leading-tight">{type.label}</h3>
                  {isSelected && <Check size={16} className="text-green-400 flex-shrink-0 mt-0.5" />}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${type.bg} ${type.iconColor} mb-2 inline-block`}>{type.badge}</span>
                <p className="text-gray-500 text-xs leading-relaxed">{type.description}</p>
              </button>
            );
          })}
        </div>
      )}

      {step === 1 && (
        <div className="flex justify-end">
          <button onClick={() => setStep(2)} className="btn-primary flex items-center gap-2">
            Configurar Widget <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* STEP 2: Configure */}
      {step === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Config Form */}
          <div className="space-y-5">
            <div className="glass-panel p-5 space-y-4">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Settings size={16} className="text-gray-400" />Configuración General
              </h3>

              {/* Name */}
              <div>
                <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Nombre del Widget *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Mi widget de chat"
                  className="input-glass w-full"
                />
              </div>

              {/* Theme */}
              <div>
                <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Tema Visual</label>
                <div className="grid grid-cols-2 gap-2">
                  {[{ id: 'dark', label: '🌙 Oscuro' }, { id: 'light', label: '☀️ Claro' }].map(t => (
                    <button
                      key={t.id}
                      onClick={() => setForm({ ...form, theme: t.id })}
                      className={`py-2.5 rounded-lg text-sm font-medium transition-all ${
                        form.theme === t.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >{t.label}</button>
                  ))}
                </div>
              </div>

              {/* Primary Color */}
              <div>
                <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Color Principal</label>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="w-12 h-10 rounded-lg cursor-pointer border-0 outline-none"
                  />
                  <input
                    type="text"
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="input-glass flex-1 font-mono text-sm"
                    placeholder="#ef4444"
                  />
                  {/* Presets */}
                  <div className="flex gap-1">
                    {['#ef4444', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b'].map(c => (
                      <button
                        key={c}
                        onClick={() => setForm({ ...form, primary_color: c })}
                        className="w-6 h-6 rounded-full border-2 transition-all hover:scale-110"
                        style={{ backgroundColor: c, borderColor: form.primary_color === c ? '#fff' : 'transparent' }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Welcome Message */}
              <div>
                <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Mensaje de Bienvenida</label>
                <input
                  type="text"
                  value={form.welcome_message}
                  onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
                  placeholder="¡Hola! ¿En qué puedo ayudarte?"
                  className="input-glass w-full"
                />
              </div>

              {/* Position (bot_button only) */}
              {form.widget_type === 'bot_button' && (
                <div>
                  <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Posición del Botón</label>
                  <div className="grid grid-cols-2 gap-2">
                    {POSITIONS.map(pos => (
                      <button
                        key={pos.id}
                        onClick={() => setForm({ ...form, position: pos.id })}
                        className={`py-2 rounded-lg text-sm flex items-center gap-2 justify-center transition-all ${
                          form.position === pos.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        <span className="text-base">{pos.icon}</span>{pos.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Button text (bot_button only) */}
              {form.widget_type === 'bot_button' && (
                <div>
                  <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Texto del Botón</label>
                  <input
                    type="text"
                    value={form.button_text}
                    onChange={(e) => setForm({ ...form, button_text: e.target.value })}
                    placeholder="Chatea con OkBot"
                    className="input-glass w-full"
                  />
                </div>
              )}

              {/* Width & Height (iframe widgets) */}
              {form.widget_type !== 'bot_button' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Ancho (px)</label>
                    <input
                      type="number"
                      value={form.width}
                      onChange={(e) => setForm({ ...form, width: e.target.value })}
                      min="300" max="800"
                      className="input-glass w-full"
                    />
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Alto (px)</label>
                    <input
                      type="number"
                      value={form.height}
                      onChange={(e) => setForm({ ...form, height: e.target.value })}
                      min="400" max="900"
                      className="input-glass w-full"
                    />
                  </div>
                </div>
              )}

              {/* Allowed Domains */}
              <div>
                <label className="text-gray-500 text-xs mb-1.5 block uppercase tracking-wider">Dominios Permitidos (opcional)</label>
                <input
                  type="text"
                  value={form.allowed_domains}
                  onChange={(e) => setForm({ ...form, allowed_domains: e.target.value })}
                  placeholder="ejemplo.com, midominio.mx"
                  className="input-glass w-full"
                />
                <p className="text-gray-600 text-xs mt-1">Separa múltiples dominios con comas. Vacío = todos permitidos.</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="btn-secondary flex items-center gap-2">
                <ArrowLeft size={14} />Atrás
              </button>
              <button onClick={handleCreate} disabled={creating} className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
                {creating ? <RefreshCw size={16} className="animate-spin" /> : <Zap size={16} />}
                {creating ? 'Creando...' : 'Crear Widget'}
              </button>
            </div>
          </div>

          {/* Live Preview */}
          <div className="space-y-4">
            <h3 className="text-white font-medium flex items-center gap-2">
              <Eye size={16} className="text-gray-400" />Vista Previa en Tiempo Real
            </h3>
            <div className="flex justify-center overflow-x-auto py-4">
              <WidgetPreview type={form.widget_type} config={form} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Embed Code Display ───────────────────────────────────────────────────────
const EmbedCodeDisplay = ({ widget, code, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Código copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="glass-panel p-6 border border-green-500/30">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-green-400 font-semibold flex items-center gap-2">
            <Code size={18} />Código de Integración — {widget?.name}
          </h3>
          <p className="text-gray-500 text-xs mt-1">Pega este código antes del cierre del tag &lt;/body&gt; en tu sitio web</p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
          <X size={18} className="text-gray-400" />
        </button>
      </div>

      <div className="bg-black/50 rounded-xl p-4 mb-4 border border-white/5">
        <pre className="text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">{code}</pre>
      </div>

      <div className="flex gap-3 items-center">
        <button
          onClick={handleCopy}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            copied ? 'bg-green-500 text-white' : 'btn-primary'
          }`}
        >
          {copied ? <><Check size={14} />¡Copiado!</> : <><Copy size={14} />Copiar Código</>}
        </button>
        <div className="flex items-center gap-2 text-gray-500 text-xs">
          <Info size={13} />
          <span>Tipo: <span className="text-gray-300">{widget?.widget_type?.replace(/_/g, ' ')}</span></span>
        </div>
      </div>
    </div>
  );
};

// ─── Main WidgetsManager ──────────────────────────────────────────────────────
const WidgetsManager = () => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'create'
  const [embedData, setEmbedData] = useState(null); // { widget, code }
  const [filterType, setFilterType] = useState('all');

  useEffect(() => { loadWidgets(); }, []);

  const loadWidgets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/widgets`);
      setWidgets(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const deleteWidget = async (id) => {
    if (!window.confirm('¿Eliminar este widget?')) return;
    try {
      await axios.delete(`${API}/widgets/${id}`);
      toast.success('Widget eliminado');
      setWidgets(prev => prev.filter(w => w.id !== id));
      if (embedData?.widget?.id === id) setEmbedData(null);
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const toggleWidget = async (widget) => {
    try {
      await axios.put(`${API}/widgets/${widget.id}`, { ...widget, is_active: !widget.is_active });
      setWidgets(prev => prev.map(w => w.id === widget.id ? { ...w, is_active: !w.is_active } : w));
      toast.success(widget.is_active ? 'Widget desactivado' : 'Widget activado');
    } catch (e) {
      toast.error('Error al actualizar');
    }
  };

  const getEmbedCode = async (widget) => {
    try {
      const res = await axios.get(`${API}/widgets/${widget.id}/embed-code`);
      setEmbedData({ widget, code: res.data.embed_code });
    } catch (e) {
      toast.error('Error al obtener código');
    }
  };

  const filtered = filterType === 'all' ? widgets : widgets.filter(w => w.widget_type === filterType);

  if (view === 'create') {
    return (
      <div>
        <WidgetCreator
          onCreated={(newWidget) => {
            setWidgets(prev => [newWidget, ...prev]);
            setView('list');
            // Auto-get embed code for the new widget
            getEmbedCode(newWidget);
          }}
          onCancel={() => setView('list')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Generador de Widgets Embebibles</h2>
          <p className="text-gray-500 text-sm mt-1">Crea widgets para integrar OkBot en cualquier sitio web</p>
        </div>
        <button onClick={() => { setEmbedData(null); setView('create'); }} className="btn-primary flex items-center gap-2">
          <Plus size={16} />Nuevo Widget
        </button>
      </div>

      {/* Widget types summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {WIDGET_TYPES.map(type => {
          const Icon = type.icon;
          const count = widgets.filter(w => w.widget_type === type.id).length;
          return (
            <button
              key={type.id}
              onClick={() => setFilterType(filterType === type.id ? 'all' : type.id)}
              className={`glass-panel p-4 text-left transition-all hover:scale-[1.02] ${
                filterType === type.id ? `border border-${type.color}-500/60` : 'border border-transparent'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg ${type.bg} flex items-center justify-center mb-2`}>
                <Icon size={16} className={type.iconColor} />
              </div>
              <p className="text-white text-xs font-medium leading-tight">{type.label}</p>
              <p className="text-gray-500 text-xs mt-0.5">{count} widget{count !== 1 ? 's' : ''}</p>
            </button>
          );
        })}
      </div>

      {/* Embed Code */}
      {embedData && (
        <EmbedCodeDisplay
          widget={embedData.widget}
          code={embedData.code}
          onClose={() => setEmbedData(null)}
        />
      )}

      {/* Filter */}
      {filterType !== 'all' && (
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-sm">Filtrando por:</span>
          <span className="text-white text-sm font-medium">{WIDGET_TYPES.find(t => t.id === filterType)?.label}</span>
          <button onClick={() => setFilterType('all')} className="text-red-400 hover:text-red-300 text-sm ml-2 flex items-center gap-1">
            <X size={12} />Quitar filtro
          </button>
        </div>
      )}

      {/* Widgets Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="text-gray-500 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <Layout size={40} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-white font-medium mb-2">
            {filterType === 'all' ? 'Sin widgets creados aún' : `Sin widgets de tipo "${WIDGET_TYPES.find(t => t.id === filterType)?.label}"`}
          </h3>
          <p className="text-gray-500 text-sm mb-6">Crea tu primer widget para integrarlo en cualquier sitio web</p>
          <button onClick={() => setView('create')} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} />Crear Primer Widget
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(widget => {
            const typeDef = WIDGET_TYPES.find(t => t.id === widget.widget_type);
            const Icon = typeDef?.icon || Layout;
            return (
              <div key={widget.id} className="glass-panel p-5 flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: widget.primary_color + '25' }}>
                      <Icon size={20} style={{ color: widget.primary_color }} />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm">{widget.name}</h4>
                      <span className={`text-xs ${typeDef?.iconColor || 'text-gray-400'}`}>
                        {typeDef?.label || widget.widget_type}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleWidget(widget)}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                    title={widget.is_active ? 'Desactivar' : 'Activar'}
                  >
                    {widget.is_active
                      ? <ToggleRight size={22} className="text-green-400" />
                      : <ToggleLeft size={22} className="text-gray-600" />
                    }
                  </button>
                </div>

                {/* Widget details */}
                <div className="space-y-1.5 mb-4 flex-1">
                  {widget.widget_type === 'bot_button' && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>Posición:</span>
                      <span className="text-gray-300">{POSITIONS.find(p => p.id === widget.position)?.label || widget.position}</span>
                    </div>
                  )}
                  {widget.widget_type !== 'bot_button' && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>Tamaño:</span>
                      <span className="text-gray-300">{widget.width || 400} × {widget.height || 600} px</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Tema:</span>
                    <span className="text-gray-300">{widget.theme === 'dark' ? 'Oscuro' : 'Claro'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: widget.primary_color || '#ef4444' }} />
                    <span className="text-gray-300 font-mono">{widget.primary_color || '#ef4444'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`px-2 py-0.5 rounded-full ${widget.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-500'}`}>
                      {widget.is_active ? '● Activo' : '○ Inactivo'}
                    </span>
                    <span className="text-gray-600">{new Date(widget.created_at).toLocaleDateString('es-MX')}</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t border-white/10">
                  <button
                    onClick={() => getEmbedCode(widget)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-medium transition-colors"
                  >
                    <Code size={13} />Ver Código
                  </button>
                  <button
                    onClick={() => deleteWidget(widget.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                    title="Eliminar widget"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Panel */}
      <div className="glass-panel p-5 border border-blue-500/20 bg-blue-500/5">
        <h3 className="text-blue-400 font-medium mb-3 flex items-center gap-2">
          <Info size={16} />Tipos de Widgets Disponibles
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {WIDGET_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <div key={type.id} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg ${type.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon size={14} className={type.iconColor} />
                </div>
                <div>
                  <p className="text-white text-xs font-semibold">{type.label}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{type.description.substring(0, 80)}...</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WidgetsManager;
